import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { useTranslation } from "react-i18next";

import { putCart } from "redux/actions/cart";

import { useAppDispatch, useAppSelector } from "hooks/redux";
import { LocaleCurrency } from "helpers/Locale";
import useLocalStorage from "../../hooks/useLocalStorage";

import Sheet from "components/sheet/Sheet";
import Input from "components/form/input/Input";
import Radio from "components/radio";
import Checkbox from "components/form/checkbox/Checkbox";
import { IRegisterGroup } from "pages/secure/facility/Admin";
import TipAllocationEditor, { TTipAllocationRow } from "components/tip/TipAllocationEditor";

import "elements/register/register.scss";
import "components/tip/tip.scss";
import classNames from "classnames";
import { IOrder } from "redux/reducers/models/order";

interface ITipState {
  tipActive: boolean;
  tipAmount: string;
  tipPercent: number;
}

interface IProps {
  totalTipAmount: number;
  handleTipTeeSheet?: (tipAmount: number, tipAllocations?: TTipAllocationRow[]) => void;
  totalCartPrice?: number;
  darkStyle?: boolean;
  order?: IOrder;
  darkerButton?: boolean;
  smallerText?: boolean;
  currency?: string;
}

// Using new component/folder that I added to the components folder
export default function Tip(props: IProps) {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const cartStore = useAppSelector(store => store.cartStore);
  const { authStore } = useAppSelector(store => store);
  const canEditTipAllocations = !!authStore.user?.permissions?.orders_edit_tip_allocations;

  const registerGroup = useLocalStorage<IRegisterGroup>("register_group");

  const [tipState, setTipState] = useState<ITipState>({
    tipActive: false,
    tipAmount: "0",
    tipPercent: null,
  });

  // Draft string so keyboard typing isn't overwritten by tip -> total recalculation
  const [editingTotalWithTip, setEditingTotalWithTip] = useState(false);
  const [totalWithTipDraft, setTotalWithTipDraft] = useState("");
  const [splitTip, setSplitTip] = useState(false);
  const [tipAllocations, setTipAllocations] = useState<TTipAllocationRow[] | null>(null);

  const cartWithoutTip = (props.totalCartPrice ?? 0) - (props.totalTipAmount ?? 0);
  const tipAmountNumber = Math.max(0, Number(tipState.tipAmount) || 0);

  function toCents(amount: number) {
    return Math.round(amount * 100) / 100;
  }

  function formatMoney(amount: number) {
    return toCents(amount).toFixed(2);
  }

  const computedTotalWithTip =
    tipState.tipAmount === "" ? "" : formatMoney(Number(tipState.tipAmount || 0) + cartWithoutTip);

  useEffect(() => {
    setTipState(prevState => ({ ...prevState, tipAmount: props.totalTipAmount?.toString() }));
  }, [props.totalTipAmount]);

  function cancelClickTip() {
    setEditingTotalWithTip(false);
    setSplitTip(false);
    setTipAllocations(null);
    setTipState(prevState => ({
      ...prevState,
      tipActive: false,
      tipAmount: tipState.tipAmount === "0" ? "0" : props.totalTipAmount.toString(),
      tipPercent: null,
    }));
  }

  async function handleAddTip() {
    const tipAmount = tipAmountNumber;
    if (splitTip && tipAmount > 0 && !tipAllocations?.length) {
      return;
    }

    const allocations = splitTip && tipAmount > 0 ? tipAllocations ?? undefined : undefined;

    if (props.handleTipTeeSheet) {
      void props.handleTipTeeSheet(tipAmount, allocations);
    } else {
      await dispatch(
        putCart(
          {
            id: cartStore.cart.id,
            total_tip: tipAmount,
            ...(canEditTipAllocations
              ? { tip_allocations: allocations ?? (tipAmount > 0 ? null : []) }
              : {}),
          },
          true,
        ),
      );
    }

    cancelClickTip();
  }

  function onClickTip(tipPercent: number) {
    const calculateTip = (props.totalCartPrice - props.totalTipAmount) * tipPercent;
    let valueOfTip = tipPercent;

    if (registerGroup?.tipping_type === "percent" || registerGroup?.tipping_type === "none" || !registerGroup) {
      valueOfTip = Math.floor(calculateTip * 100) / 100;
    }

    setEditingTotalWithTip(false);
    setTipState(prevState => ({
      ...prevState,
      tipAmount: toCents(valueOfTip).toFixed(2),
      tipPercent,
    }));
  }

  function handleTipChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;
    setEditingTotalWithTip(false);

    // Allow empty value while typing
    if (value === "") {
      setTipState(prev => ({
        ...prev,
        tipAmount: "",
        tipPercent: null,
      }));
      return;
    }

    // Allow partial decimals while typing (e.g. "1.", "1.5")
    if (!/^\d*\.?\d{0,2}$/.test(value)) {
      return;
    }

    const numericValue = Number(value);

    // Guard against NaN and negatives
    if (!Number.isNaN(numericValue) && numericValue >= 0) {
      setTipState(prev => ({
        ...prev,
        tipAmount: value,
        tipPercent: null,
      }));
    }
  }

  function handleTotalWithTipChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;

    if (value !== "" && !/^\d*\.?\d{0,2}$/.test(value)) {
      return;
    }

    setTotalWithTipDraft(value);

    if (value === "" || value === ".") {
      setTipState(prev => ({
        ...prev,
        tipAmount: "",
        tipPercent: null,
      }));
      return;
    }

    const numericValue = Number(value);

    // Tip is total entered minus pre-tip cart
    if (!Number.isNaN(numericValue) && numericValue >= 0) {
      const tip = Math.max(0, toCents(numericValue - cartWithoutTip));
      setTipState(prev => ({
        ...prev,
        tipAmount: tip.toFixed(2),
        tipPercent: null,
      }));
    }
  }

  function handleTotalWithTipFocus(e: React.ChangeEvent<HTMLInputElement>) {
    setEditingTotalWithTip(true);
    setTotalWithTipDraft(computedTotalWithTip);
    e.target.select();
  }

  function handleTotalWithTipBlur() {
    setEditingTotalWithTip(false);
    if (tipState.tipAmount !== "") {
      setTipState(prev => ({
        ...prev,
        tipAmount: toCents(Number(prev.tipAmount) || 0).toFixed(2),
      }));
    }
  }


  const okDisabled = splitTip && tipAmountNumber > 0 && !tipAllocations?.length;

  return (
    <div>
      <div className="flex">
        {props.totalTipAmount > 0 || props?.order || cartStore?.cart?.status === "void" ? (
          <div
            className={classNames("ml-auto", { "cursor-pointer": !props.order })}
            onClick={
              cartStore?.cart?.status !== "complete" && !props?.order && cartStore?.cart?.status !== "void"
                ? () => setTipState(prevState => ({ ...prevState, tipActive: true }))
                : undefined
            }
          >
            <LocaleCurrency currency="cad" amount={props.totalTipAmount} />
          </div>
        ) : (
          cartStore?.cart?.status !== "complete" && (
            <div
              className="cursor-pointer"
              onClick={() => setTipState(prevState => ({ ...prevState, tipActive: true }))}
            >
              <span style={{ fontWeight: "600", color: "#203254", fontSize: props.smallerText ? "14px" : null }}>
                {" "}
                <FontAwesomeIcon icon={faPlus} /> {t("Add Tip")}
              </span>
            </div>
          )
        )}
      </div>

      {tipState.tipActive ? (
        <Sheet
          title={t("Add Tip")}
          open={tipState.tipActive}
          size={splitTip ? "medium" : "small"}
          closable
          onCancel={cancelClickTip}
          onOk={handleAddTip}
          okText={t("Add Tip")}
          okDisabled={okDisabled}
          darkStyle={props?.darkStyle ? true : false}
          darkerButton={props.darkerButton}
        >
          <div className="tip-container">
            <Input
              label={t("Tip Amount")}
              type="number"
              min={0}
              step="0.01"
              value={tipState.tipAmount}
              autoFocus
              onFocus={(e: React.ChangeEvent<HTMLInputElement>) => e.target.select()}
              onChange={handleTipChange}
              labelStyle={{ color: props?.darkStyle && "#FFFFFF" }}
            />
            <div className="tip-radio-container">
              <Radio.Group
                className="tip-radio-group"
                darkStyle={props?.darkStyle ? true : false}
                name="tips"
                onChange={onClickTip}
                value={tipState.tipPercent}
              >
                {registerGroup?.tipping_type === "percent" ? (
                  <>
                    <Radio.Button value={registerGroup?.tip_percentage_low / 100}>
                      {`${registerGroup?.tip_percentage_low}%`}{" "}
                    </Radio.Button>
                    <Radio.Button value={registerGroup?.tip_percentage_mid / 100}>
                      {`${registerGroup?.tip_percentage_mid}%`}{" "}
                    </Radio.Button>
                    <Radio.Button value={registerGroup?.tip_percentage_high / 100}>
                      {`${registerGroup?.tip_percentage_high}%`}{" "}
                    </Radio.Button>
                  </>
                ) : registerGroup?.tipping_type === "fixed_amount" ? (
                  <>
                    <Radio.Button value={registerGroup?.tip_fixed_amount_low}>
                      <LocaleCurrency currency={props.currency ?? "cad"} amount={registerGroup?.tip_fixed_amount_low} />
                    </Radio.Button>
                    <Radio.Button value={registerGroup?.tip_fixed_amount_mid}>
                      <LocaleCurrency currency={props.currency ?? "cad"} amount={registerGroup?.tip_fixed_amount_mid} />
                    </Radio.Button>
                    <Radio.Button value={registerGroup?.tip_fixed_amount_high}>
                      <LocaleCurrency currency={props.currency ?? "cad"} amount={registerGroup?.tip_fixed_amount_high} />
                    </Radio.Button>
                  </>
                ) : (
                  <>
                    <Radio.Button value={0.1}>{"10%"}</Radio.Button>
                    <Radio.Button value={0.15}>{"15%"}</Radio.Button>
                    <Radio.Button value={0.2}>{"20%"}</Radio.Button>
                  </>
                )}
              </Radio.Group>
            </div>
            <div className="tip-divider">
              <div className="tip-divider-line" />
              <div className="tip-divider-text">or</div>
              <div className="tip-divider-line" />
            </div>
            <Input
              label={t("Total With Tip")}
              type="number"
              min={0}
              step="0.01"
              value={editingTotalWithTip ? totalWithTipDraft : computedTotalWithTip}
              onFocus={handleTotalWithTipFocus}
              onBlur={handleTotalWithTipBlur}
              onChange={handleTotalWithTipChange}
              labelStyle={{ color: props?.darkStyle && "#FFFFFF" }}
            />
            {tipAmountNumber > 0 && canEditTipAllocations ? (
              <div className="mt-4">
                <Checkbox
                  id="split-tip"
                  size="medium"
                  checked={splitTip}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSplitTip(e.target.checked);
                    if (!e.target.checked) {
                      setTipAllocations(null);
                    }
                  }}
                  label={t("Split tip among servers")}
                />
                {splitTip ? (
                  <div className="mt-3">
                    <TipAllocationEditor
                      tipTotal={tipAmountNumber}
                      onChange={setTipAllocations}
                      currency={props.currency}
                      darkStyle={props.darkStyle}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </Sheet>
      ) : null}
    </div>
  );
}